import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectPoi, selectPoiLocations, selectAtsBreakerSizes } from '../../../store/selectors/databaseSelectors';
import { addPoi, addPoiLocation } from '../../../store/slices/databaseSlice';
// import { addAtsBreakerSize } from '../../../store/slices/databaseSlice';
import { poiService, poiLocationsService } from '../../../services';
import { Poi, PoiLocation, AtsBreakerSize } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface PoiMethodForm {
  model: string;
}

interface PoiLocationForm {
  poi_id: string;
  location_name: string;
}

// interface AtsBreakerSizeForm {
//   amperage: string;
// }

type PoiTab = 'methods' | 'locations' | 'ats_breakers';

const POI_TABS: { id: PoiTab; label: string }[] = [
  { id: 'methods', label: 'POI Methods' },
  { id: 'locations', label: 'POI Locations' }
  // { id: 'ats_breakers', label: 'ATS Breaker Sizes' }
];

export const PoiManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const poiMethods = useAppSelector(selectPoi);
  const poiLocations = useAppSelector(selectPoiLocations);
  const atsBreakerSizes = useAppSelector(selectAtsBreakerSizes);
  
  // Form states
  const [methodForm, setMethodForm] = useState<PoiMethodForm>({ model: '' });
  const [locationForm, setLocationForm] = useState<PoiLocationForm>({ poi_id: '', location_name: '' });
  // const [atsBreakerForm, setAtsBreakerForm] = useState<AtsBreakerSizeForm>({ amperage: '' });
  
  // UI states
  const [activeTab, setActiveTab] = useState<PoiTab>('methods');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localData, setLocalData] = useState({
    methods: poiMethods,
    locations: poiLocations,
    ats_breakers: atsBreakerSizes
  });
  const [selectedPoiOption, setSelectedPoiOption] = useState<{id: string, label: string} | null>(null);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalData({
      methods: poiMethods,
      locations: poiLocations,
      ats_breakers: atsBreakerSizes
    });
  }, [poiMethods, poiLocations, atsBreakerSizes]);

  // Create POI options for dropdown
  const poiOptions = localData.methods.map(poi => ({
    id: poi.id,
    label: poi.model
  }));

  // Handle POI selection for locations
  const handlePoiSelect = (option: {id: string, label: string} | null) => {
    setSelectedPoiOption(option);
    setLocationForm(prev => ({ 
      ...prev, 
      poi_id: option?.id || '' 
    }));
  };

  // Get current data for active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'methods': return localData.methods;
      case 'locations': return localData.locations;
      case 'ats_breakers': return localData.ats_breakers;
      default: return [];
    }
  };

  const handleMethodSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!methodForm.model.trim()) {
      toast('Please enter a POI method', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newPoi = await poiService.createPoi({
        model: methodForm.model.trim().toUpperCase()
      });
      
      // Update Redux state
      dispatch(addPoi(newPoi));
      
      // Update local state
      setLocalData(prev => ({ ...prev, methods: [...prev.methods, newPoi] }));
      
      // Reset form
      setMethodForm({ model: '' });
      
      toast.success('POI method created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating POI method:', error);
      toast.error('Failed to create POI method. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locationForm.poi_id || !locationForm.location_name.trim()) {
      toast('Please select a POI method and enter a location name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newLocation = await poiLocationsService.createPoiLocation({
        poi_id: locationForm.poi_id,
        location_name: locationForm.location_name.trim().toUpperCase()
      });
      
      // If the backend doesn't return the poi relation, add it from local data
      if (!newLocation.poi && newLocation.poi_id) {
        const poi = localData.methods.find(p => p.id === newLocation.poi_id);
        if (poi) {
          newLocation.poi = poi;
        }
      }
      
      // Update Redux state
      dispatch(addPoiLocation(newLocation));
      
      // Update local state
      setLocalData(prev => ({ ...prev, locations: [...prev.locations, newLocation] }));
      
      // Reset form
      setLocationForm({ poi_id: '', location_name: '' });
      setSelectedPoiOption(null);
      
      toast.success('POI location created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating POI location:', error);
      toast.error('Failed to create POI location. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleAtsBreakerSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!atsBreakerForm.amperage.trim()) {
  //     toast('Please enter an amperage value', TOAST_WARNING_CONFIG);
  //     return;
  //   }

  //   setIsSubmitting(true);
  //   try {
  //     const newAtsBreakerSize = await poiLocationsService.createAtsBreakerSize({
  //       amperage: parseInt(atsBreakerForm.amperage.trim())
  //     });
      
  //     // Update Redux state
  //     dispatch(addAtsBreakerSize(newAtsBreakerSize));
      
  //     // Update local state
  //     setLocalData(prev => ({ ...prev, ats_breakers: [...prev.ats_breakers, newAtsBreakerSize] }));
      
  //     // Reset form
  //     setAtsBreakerForm({ amperage: '' });
      
  //     toast.success('ATS breaker size created successfully!', TOAST_CONFIG.toastOptions.success);
  //   } catch (error) {
  //     console.error('Error creating ATS breaker size:', error);
  //     toast.error('Failed to create ATS breaker size. Please try again.', TOAST_CONFIG.toastOptions.error);
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  const renderForm = () => {
    switch (activeTab) {
      case 'methods':
        return (
          <form onSubmit={handleMethodSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                POI Method *
              </label>
              <input
                type="text"
                value={methodForm.model}
                onChange={(e) => setMethodForm({ model: e.target.value })}
                placeholder="e.g., Breaker Tie-In"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-slate-500 mt-1">Text will be automatically converted to uppercase.</p>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !methodForm.model.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : 'Create POI Method'}
            </button>
          </form>
        );

      case 'locations':
        return (
          <form onSubmit={handleLocationSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                POI Method *
              </label>
              <SearchDropdown
                options={poiOptions}
                onSelect={handlePoiSelect}
                placeholder="Select POI method..."
                value={selectedPoiOption}
                className="text-sm"
                inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                zIndex={1000}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Location Name *
              </label>
              <input
                type="text"
                value={locationForm.location_name}
                onChange={(e) => setLocationForm(prev => ({ ...prev, location_name: e.target.value }))}
                placeholder="e.g., Main Panel"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-slate-500 mt-1">Text will be automatically converted to uppercase.</p>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !locationForm.poi_id || !locationForm.location_name.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : 'Create POI Location'}
            </button>
          </form>
        );

      // case 'ats_breakers':
      //   return (
      //     <form onSubmit={handleAtsBreakerSubmit} className="space-y-3">
      //       <div>
      //         <label className="block text-xs font-medium text-slate-700 mb-1">
      //           Amperage *
      //         </label>
      //         <div className="relative">
      //           <input
      //             type="number"
      //             min="1"
      //             value={atsBreakerForm.amperage}
      //             onChange={(e) => setAtsBreakerForm({ amperage: e.target.value })}
      //             placeholder="e.g., 200"
      //             className="w-full px-3 py-2 pr-8 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      //             required
      //           />
      //           <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-slate-500">A</span>
      //         </div>
      //         <p className="text-xs text-slate-500 mt-1">Enter numeric value only. "A" will be appended automatically.</p>
      //       </div>
            
      //       <button
      //         type="submit"
      //         disabled={isSubmitting || !atsBreakerForm.amperage.trim()}
      //         className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
      //       >
      //         {isSubmitting ? 'Creating...' : 'Create ATS Breaker Size'}
      //       </button>
      //     </form>
      //   );

      default:
        return null;
    }
  };

  const renderTags = () => {
    const currentData = getCurrentData();
    
    if (currentData.length === 0) {
      return (
        <p className="text-slate-500 text-xs text-center py-8">
          No {POI_TABS.find(t => t.id === activeTab)?.label?.toLowerCase()} found
        </p>
      );
    }

    switch (activeTab) {
      case 'methods':
        return (
          <div className="flex flex-wrap gap-2 p-2">
            {(currentData as Poi[]).map((poi) => (
              <div key={poi.id} className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-800 rounded-full border border-purple-200 hover:bg-purple-100 transition-colors">
                <span className="text-xs font-medium">{poi.model}</span>
              </div>
            ))}
          </div>
        );

      case 'locations':
        // Group locations by location name
        const groupedLocations = (currentData as PoiLocation[]).reduce((acc, location) => {
          const locationName = location.location_name;
          if (!acc[locationName]) {
            acc[locationName] = [];
          }
          acc[locationName].push(location.poi?.model || 'Unknown Method');
          return acc;
        }, {} as Record<string, string[]>);

        return (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Location</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">POI Methods</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedLocations).sort().map((locationName) => (
                  <tr key={locationName} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-slate-800">{locationName}</td>
                    <td className="py-2 px-3 text-slate-600">{groupedLocations[locationName].join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'ats_breakers':
        return (
          <div className="flex flex-wrap gap-2 p-2">
            {(currentData as AtsBreakerSize[]).map((atsBreaker) => (
              <div key={atsBreaker.id} className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-800 rounded-full border border-purple-200 hover:bg-purple-100 transition-colors">
                <span className="text-xs font-medium">{atsBreaker.amperage}A</span>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const currentData = getCurrentData();

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="flex flex-wrap border-b border-slate-200 mb-4">
        {POI_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 mr-4 mb-2 ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label} ({currentData.length})
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New {POI_TABS.find(t => t.id === activeTab)?.label?.replace('POI ', '').replace('ATS ', '')}
          </h3>

          {renderForm()}
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing {POI_TABS.find(t => t.id === activeTab)?.label}
            </h3>
            <span className="text-xs text-slate-500">
              {currentData.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {renderTags()}
          </div>
        </div>
      </div>
    </div>
  );
};